import { useAuth } from '@clerk/react'
import { ClerkOfflineError } from '@clerk/react/errors'
import type { AppRouter } from '@buyerflo/functions'
import { queryClient, TRPCProvider } from '../utils/trpc'
import { QueryClientProvider } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import * as React from 'react'
import { TRPC_URL } from '../utils/env'

type AppTRPCProviderProps = {
    children: React.ReactNode
}

export function AppTRPCProvider({ children }: AppTRPCProviderProps) {
    const { getToken } = useAuth()

    const [trpcClient] = React.useState(() =>
        createTRPCClient<AppRouter>({
            links: [
                httpBatchLink({
                    url: TRPC_URL,
                    async headers() {
                        // Called on every request/batch, so it stays fresh
                        let token: string | null = null
                        try {
                            token = await getToken()
                        } catch (err) {
                            if (ClerkOfflineError.is(err)) return {}
                            throw err
                        }
                        // Extract orgId from URL path (pattern: /:orgId/...)
                        // Validate it looks like a UUID to avoid sending route names as orgId
                        const pathSegment = window.location.pathname.split('/')[1] || ''
                        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
                        const orgId = UUID_RE.test(pathSegment) ? pathSegment : ''
                        return {
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            ...(orgId ? { 'x-org-id': orgId } : {}),
                            // Authoritative browser origin (exact scheme + port), so the
                            // server can build redirect URLs back to THIS host. The
                            // inferred Origin/Referer header isn't reliable cross-origin.
                            'x-app-origin': window.location.origin,
                        }
                    },
                }),
            ],
        }),
    )

    return (
        <QueryClientProvider client={queryClient}>
            <TRPCProvider
                trpcClient={trpcClient}
                queryClient={queryClient}
            >
                {children}
            </TRPCProvider>
        </QueryClientProvider>
    )
}
